import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import type { TextFieldProps } from '@mui/material/TextField';
import type { List } from 'immutable';
import React, { useEffect, useId, useMemo, useState } from 'react';

import { payeesSelectors } from 'app-components/flux/payees';
import { transactionsActions, transactionsSelectors } from 'app-components/flux/transactions';
import { useFetchOnce } from 'app-components/hooks/utils';
import { useAppSelector } from 'hooks/redux';
import {
  filterPayeeOptions,
  getPayeeOptionLabel,
  getPayeeOptions,
  groupByPayeeOption,
  isNewPayeeOption,
  isPayeeOptionEqualToValue,
} from 'components/SPayeeField/payeeSuggestions';

type Props = {
  value: string;
  onChange: (event: { target: { value: string } }) => void;
  error: boolean;
  helperText?: string;
  textFieldProps?: TextFieldProps;
};

export default function SeriesPayeeField({
  value,
  onChange,
  error,
  helperText,
  textFieldProps,
}: Props): React.JSX.Element {
  useFetchOnce(transactionsActions.getTransactions, transactionsSelectors);
  const [inputValue, setInputValue] = useState(value || '');
  const [open, setOpen] = useState(false);
  const payeesList = useAppSelector(payeesSelectors.getPayeesNamesSorted) as List<string>;
  const options = useMemo(() => getPayeeOptions(payeesList.toArray(), inputValue), [payeesList, inputValue]);
  const fieldId = useId();

  useEffect(() => setInputValue(value || ''), [value]);

  return (
    <Autocomplete
      id={fieldId}
      value={value || ''}
      inputValue={inputValue}
      open={open}
      onClose={() => setOpen(false)}
      options={options}
      autoHighlight
      autoSelect
      freeSolo
      fullWidth
      disableClearable
      groupBy={groupByPayeeOption}
      getOptionLabel={getPayeeOptionLabel}
      filterOptions={filterPayeeOptions}
      isOptionEqualToValue={isPayeeOptionEqualToValue}
      renderGroup={(params) => (
        <li key={params.key}>
          <Box component='div' sx={{ px: 1, pt: 1, pb: 0.5, typography: 'caption' }}>
            {params.group}
          </Box>
          <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
        </li>
      )}
      renderOption={(props, option) => (
        <li {...props} style={{ marginBottom: typeof option !== 'string' && isNewPayeeOption(option) ? 8 : 0 }}>
          {typeof option === 'string' ? option : option.label}
        </li>
      )}
      onInputChange={(_event, nextValue, reason) => {
        if (reason !== 'reset') {
          setInputValue(nextValue);
          setOpen(true);
          onChange({ target: { value: nextValue } });
        }
      }}
      onChange={(_event, nextValue) => {
        const nextPayee = typeof nextValue === 'string' ? nextValue : nextValue?.label || '';
        setInputValue(nextPayee);
        setOpen(false);
        onChange({ target: { value: nextPayee } });
      }}
      renderInput={(params) => {
        // Autocomplete exposes the anchor `ref` and adornments for the input's wrapper via
        // `params.InputProps`, and the `ref`/handlers for the underlying `<input>` tag itself via
        // `params.inputProps`. TextField's `slotProps.input`/`slotProps.htmlInput` replace rather
        // than merge, so fold both `params` objects in ourselves instead of losing those refs.
        const { slotProps, ...restTextFieldProps } = textFieldProps ?? {};
        const { input: slotPropsInput, htmlInput: slotPropsHtmlInput, ...restSlotProps } = slotProps ?? {};
        return (
          <TextField
            {...params}
            label='Name'
            variant='filled'
            fullWidth
            autoFocus
            onFocus={(event: React.FocusEvent<HTMLInputElement>) => event.target.select()}
            {...restTextFieldProps}
            error={error}
            helperText={helperText}
            slotProps={{
              ...restSlotProps,
              input: { ...params.InputProps, ...slotPropsInput, autoComplete: 'off' },
              htmlInput: { ...params.inputProps, ...slotPropsHtmlInput },
            }}
          />
        );
      }}
    />
  );
}
